/**
 * Pixel Registry
 *
 * Central registry for managing pixel configurations and adapters.
 * Provides methods to query which pixels are active and retrieve their adapters.
 *
 * Firing Modes:
 * - browser_only: Fire only in browser (Microsoft Ads, Taboola)
 * - server_only: Fire only server-side, suppress browser pixel (admin choice)
 * - both: Fire both browser and server with event_id for deduplication (Facebook, TikTok)
 * - browser_or_server: Fire one or the other, never both (GA4 purchase)
 *
 * @since 1.51.0
 */

(function (pmw, $, undefined) {

	/**
	 * Valid firing modes
	 * @type {string[]}
	 */
	const VALID_FIRING_MODES = ["browser_only", "server_only", "both", "browser_or_server"];

	/**
	 * Default firing mode for pixels that don't specify one
	 * @type {string}
	 */
	const DEFAULT_FIRING_MODE = "browser_only";

	/**
	 * Pixel Registry namespace
	 */
	pmw.pixelRegistry = {

		/**
		 * Get all active pixels (both browser-only and S2S-capable)
		 *
		 * Queries each registered adapter's isActive function to determine
		 * if the pixel should be loaded based on pmwDataLayer configuration.
		 *
		 * @returns {string[]} Array of active pixel names
		 */
		getActivePixels() {
			const activePixels = [];

			if (!pmw.pixelCapabilities) {
				return activePixels;
			}

			// Check each registered pixel's isActive function
			for (const [pixelName, capabilities] of Object.entries(pmw.pixelCapabilities)) {
				if (typeof capabilities.isActive === "function") {
					try {
						if (capabilities.isActive()) {
							activePixels.push(pixelName);
						}
					} catch (e) {
						pmw.console.error(`Error checking if ${pixelName} is active:`, e);
					}
				}
			}

			return activePixels;
		},

		/**
		 * Get browser-only pixels (pixels that don't support S2S)
		 *
		 * @returns {string[]} Array of browser-only pixel names
		 */
		getActiveBrowserOnlyPixels() {
			const browserPixels = [];
			const activePixels  = this.getActivePixels();

			for (const pixelName of activePixels) {
				// If pixel doesn't support S2S, it's browser-only
				if (pmw.pixelCapabilities?.[pixelName]?.supportsS2S !== true) {
					browserPixels.push(pixelName);
				}
			}

			return browserPixels;
		},

		/**
		 * Get S2S-capable pixels
		 *
		 * Returns pixels that declared supportsS2S capability when registering.
		 * These pixels ALWAYS receive the pmw:s2s:* events to fire their browser pixels (fbq, ttq, etc.),
		 * regardless of whether S2S is actually enabled. They handle their own S2S logic internally.
		 *
		 * @returns {string[]} Array of S2S pixel names
		 */
		getActiveS2SPixels() {
			const s2sPixels    = [];
			const activePixels = this.getActivePixels();

			// Check each active pixel's capabilities
			for (const pixelName of activePixels) {
				// Check if this pixel declared supportsS2S capability when registering
				if (pmw.pixelCapabilities?.[pixelName]?.supportsS2S === true) {
					s2sPixels.push(pixelName);
				}
			}

			return s2sPixels;
		},

		/**
		 * Get adapter for a specific pixel
		 *
		 * @param {string} pixelName - The pixel name (e.g., 'facebook', 'google_ads')
		 * @returns {object|null} The adapter object or null if not found
		 */
		getAdapter(pixelName) {
			if (!pmw.adapters) {
				return null;
			}

			return pmw.adapters[pixelName] || null;
		},

		/**
		 * Get active pixels with a specific capability
		 *
		 * Returns pixels that have the specified capability flag set to true.
		 * This is a generic method that allows querying any capability
		 * without needing dedicated methods for each one.
		 *
		 * @param {string} capabilityName - The capability to check (e.g., 'supportsS2S', 'supportsPageViewS2S')
		 * @returns {string[]} Array of pixel names with the specified capability
		 *
		 * @example
		 * // Get all pixels that support page view S2S
		 * pmw.pixelRegistry.getPixelsWithCapability('supportsPageViewS2S')
		 * // Returns: ['facebook', 'snapchat', 'reddit']
		 */
		getPixelsWithCapability(capabilityName) {
			const pixelsWithCapability = [];
			const activePixels         = this.getActivePixels();

			for (const pixelName of activePixels) {
				if (pmw.pixelCapabilities?.[pixelName]?.[capabilityName] === true) {
					pixelsWithCapability.push(pixelName);
				}
			}

			return pixelsWithCapability;
		},

		/**
		 * Get the firing mode for a specific pixel
		 *
		 * Returns the registered firing mode for the pixel. The mode determines
		 * whether events fire in browser, server, or both.
		 *
		 * @param {string} pixelName - The pixel name
		 * @returns {string} The firing mode (browser_only, server_only, both, browser_or_server)
		 */
		getFiringMode(pixelName) {
			const mode = pmw.pixelCapabilities?.[pixelName]?.mode;

			// Validate mode is one of the allowed values
			if (mode && VALID_FIRING_MODES.includes(mode)) {
				return mode;
			}

			// Legacy support: if supportsS2S is true but no mode specified, default to 'both'
			if (pmw.pixelCapabilities?.[pixelName]?.supportsS2S === true) {
				return "both";
			}

			return DEFAULT_FIRING_MODE;
		},

		/**
		 * Determine whether browser and/or server should fire for a pixel/event combination
		 *
		 * This is the core method for the unified firing mode system. It considers:
		 * - The pixel's registered firing mode
		 * - Whether S2S is globally enabled
		 * - Any filters that modify firing behavior
		 *
		 * @param {string} pixelName - The pixel name
		 * @param {string} eventName - The event name (e.g., 'add_to_cart', 'purchase')
		 * @returns {object} { fire_browser: boolean, fire_server: boolean }
		 */
		getFiringDecision(pixelName, eventName) {
			const mode        = this.getFiringMode(pixelName);
			const s2sEnabled  = pmwDataLayer?.general?.server_2_server?.active === true;
			const supportsS2S = pmw.pixelCapabilities?.[pixelName]?.supportsS2S === true;

			let decision = {
				fire_browser: true,
				fire_server : false,
			};

			switch (mode) {
				case "browser_only":
					decision = {fire_browser: true, fire_server: false};
					break;

				case "server_only":
					// Server only fires if S2S is enabled AND pixel supports it
					decision = {
						fire_browser: false,
						fire_server : s2sEnabled && supportsS2S,
					};
					break;

				case "both":
					// Browser always fires, server fires if enabled
					decision = {
						fire_browser: true,
						fire_server : s2sEnabled && supportsS2S,
					};
					break;

				case "browser_or_server":
					// If S2S enabled and supported, use server; otherwise browser
					if (s2sEnabled && supportsS2S) {
						decision = {fire_browser: false, fire_server: true};
					} else {
						decision = {fire_browser: true, fire_server: false};
					}
					break;
			}

			// Apply filter to allow external modification of firing behavior
			// Use case: Force server-only for specific pixels, disable certain pixels per-event
			decision = pmw.hooks.applyFilters(
				"pmw_pixel_firing_decision",
				decision,
				pixelName,
				eventName,
				mode,
			);

			return decision;
		},

		/**
		 * Get all pixels that should fire in browser for a specific event
		 *
		 * @param {string} eventName - The event name
		 * @returns {string[]} Array of pixel names that should fire in browser
		 */
		getPixelsForBrowserFiring(eventName) {
			const pixels       = [];
			const activePixels = this.getActivePixels();

			for (const pixelName of activePixels) {
				const decision = this.getFiringDecision(pixelName, eventName);
				if (decision.fire_browser) {
					pixels.push(pixelName);
				}
			}

			return pixels;
		},

		/**
		 * Get all pixels that should fire server-side for a specific event
		 *
		 * @param {string} eventName - The event name
		 * @returns {string[]} Array of pixel names that should fire server-side
		 */
		getPixelsForServerFiring(eventName) {
			const pixels       = [];
			const activePixels = this.getActivePixels();

			for (const pixelName of activePixels) {
				const decision = this.getFiringDecision(pixelName, eventName);
				if (decision.fire_server) {
					pixels.push(pixelName);
				}
			}

			return pixels;
		},
	};

}(window.pmw = window.pmw || {}, jQuery));